import React, { useEffect, useState } from "react";
import { BASE_URL } from "../../../../config";
import { apiFetch } from "../../../../apiFetch";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import "./ContractOffer.css";
import OfferNeonPanel from "../../../OfferNeonPanel";

// ✅ Payment helper (matches ManageContract behavior)
const isPaid = (offer) => String(offer?.paymentStatus || "").toLowerCase() === "paid";

// ✅ Activation start time (paid time). Safe fallbacks in case backend field differs.
const getActivationStartTimeMs = (offer) => {
    const t =
        offer?.paidAt ||
        offer?.activatedAt ||
        offer?.paymentPaidAt ||
        offer?.paymentCompletedAt ||
        offer?.paymentSuccessTime ||
        offer?.activationTime ||
        null;

    if (!t) return null;
    const ms = new Date(t).getTime();
    return Number.isNaN(ms) ? null : ms;
};

const isOfferActive = (offer) => {
    if (!offer) return false;

    // must be accepted + admin approved
    if (offer.status !== "accepted") return false;
    if (offer.adminApproval !== "approved") return false;

    // NEW: must be paid/activated
    if (!isPaid(offer)) return false;

    // if backend sends paidAt, enforce it
    const startMs = getActivationStartTimeMs(offer);
    if (!startMs) return false;

    // expiryDate should be computed server-side; if it exists, use it
    if (!offer.expiryDate) return false;

    return new Date(offer.expiryDate).getTime() > Date.now();
};

const toAbsoluteUrl = (maybePathOrUrl) => {
    if (!maybePathOrUrl) return "#";
    if (/^https?:\/\//i.test(maybePathOrUrl)) return maybePathOrUrl;
    try {
        return new URL(maybePathOrUrl, BASE_URL).toString();
    } catch {
        return "#";
    }
};

// Removes leading "Offer to Sign:" (and variants with different spacing/case) from stored titles.
// Prevents UI duplication like "Offer to Sign: Offer to Sign: Limited Volto"
const normalizeOfferTitle = (rawTitle) => {
    if (!rawTitle) return "";
    return String(rawTitle).replace(/^\s*offer\s*to\s*sign\s*:\s*/i, "").trim();
};

const ContractOffer = ({ role, userId, agentId, scoutId, clubId }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const normalizedRole = (role || "agent").toLowerCase();

    const state = location.state || {};
    const effectiveId =
        userId ||
        agentId ||
        scoutId ||
        clubId ||
        state.userId ||
        state.agentId ||
        state.scoutId ||
        state.clubId ||
        null;

    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);

    const navigateToProfile = (playerSlug) => {
        if (!playerSlug) return;
        const roleForRoute = normalizedRole;
        navigate(`/${roleForRoute}-dashboard/player-profile/${playerSlug}`, {
            state: {
                viewerRole: roleForRoute,
                viewerId: effectiveId || null,
                from: "contract-offers",
            },
        });
    };

    useEffect(() => {
        const fetchOffers = async () => {
            if (!effectiveId) {
                setLoading(false);
                return;
            }

            try {
                const res = await apiFetch(`/api/offers/owner/${effectiveId}`, {
                    method: "GET",
                });

                const data = await res.json();

                if (!res.ok) {
                    setOffers([]);
                    return;
                }

                if (data.success) {
                    setOffers(data.offers);
                }
            } catch (err) {
                console.error("Error fetching offers:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchOffers();
    }, [effectiveId]);

    const getStatusLabel = (offer) => {
        switch (offer.status) {
            case "pending":
                return "Pending player response";
            case "accepted":
                return "Accepted by player";
            case "rejected":
                return "Rejected by player";
            case "terminated":
                return "Contract terminated";
            case "withdrawn":
                return "Offer withdrawn";
            default:
                return offer.status || "Unknown";
        }
    };

    const shouldShowAdminApproval = (offer) =>
        offer.status === "accepted" && !!offer.adminApproval;

    const handleWithdraw = async (offerId, status) => {
        if (status !== "pending") {
            alert("❌ Offer already accepted. Use Manage Contracts.");
            return;
        }

        const confirmWithdraw = window.confirm("Withdraw this offer?");
        if (!confirmWithdraw) return;

        try {
            const res = await apiFetch(`/api/offers/${offerId}/withdraw`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}),
            });

            const result = await res.json();

            if (result.success) {
                if (result.offer) {
                    setOffers((prev) =>
                        prev.map((o) =>
                            o.id === result.offer.id ? { ...o, ...result.offer } : o
                        )
                    );
                } else {
                    setOffers((prev) =>
                        prev.map((o) => (o.id === offerId ? { ...o, status: "withdrawn" } : o))
                    );
                }
            }
        } catch (err) {
            console.error(err);
        }
    };

    // ✅ FIXED: total days/hours/minutes (moment.duration().days() is remainder days, not total)
    const renderCountdown = (expiryDate) => {
        if (!expiryDate) return null;

        const end = moment(expiryDate);
        const ms = end.diff(moment());

        if (ms <= 0) {
            return <span style={{ color: "#EF5350" }}>⏳ Contract expired</span>;
        }

        const totalSeconds = Math.floor(ms / 1000);
        const days = Math.floor(totalSeconds / (60 * 60 * 24));
        const hours = Math.floor((totalSeconds / (60 * 60)) % 24);
        const minutes = Math.floor((totalSeconds / 60) % 60);

        return (
            <span style={{ color: "#4FC3F7" }}>
                ⏳ {days}d {hours}h {minutes}m
            </span>
        );
    };

    const titleByRole = {
        agent: "Agent Contract Offers",
        scout: "Scout Contract Offers",
        club: "Club Contract Offers",
    };

    const title = titleByRole[normalizedRole] || "Contract Offers";

    return (
        <div className="contract-offer-container">
            <h2 className="contract-offer-title">{title}</h2>
            <p className="contract-offer-subtitle">
                These are contract offers you made to the players:
            </p>

            {loading ? (
                <p className="loading-message">Loading offers...</p>
            ) : offers.length === 0 ? (
                <p className="empty-message">No contract offers submitted yet.</p>
            ) : (
                offers.map((offer) => {
                    const playerSlug =
                        offer.playerProfileSlug ||
                        offer.profileSlug ||
                        offer.playerSlug ||
                        offer.slug ||
                        null;

                    const profileUrl = playerSlug
                        ? `/${normalizedRole}-dashboard/player-profile/${playerSlug}`
                        : null;

                    const active = isOfferActive(offer);

                    // Prevent duplicated "Offer to Sign:" by stripping it from stored titles.
                    const cleanedTitle = normalizeOfferTitle(offer.title);
                    const displayTitle = cleanedTitle || offer.title || "Offer to Sign";

                    // Conditionally show "Reason" only if present (avoid "Reason: —")
                    const hasWithdrawReason =
                        typeof offer.withdrawReason === "string" &&
                        offer.withdrawReason.trim().length > 0;

                    return (
                        <div key={offer.id} style={{ marginBottom: "30px" }}>
                            <OfferNeonPanel
                                offer={{
                                    playerName: displayTitle,
                                    playerUsername: offer.playerUsername,
                                    contractDuration: offer.contractDuration,
                                    additionalNotes: offer.additionalNotes,
                                    playerProfileUrl: profileUrl,
                                    contractPolicyUrl: "/VSN_Participant_Contract.docx",
                                    uploadedContractUrl:
                                        offer.contractFile || offer.contractUrl || offer.agentContractUrl,
                                    statusText: `${getStatusLabel(offer)}${shouldShowAdminApproval(offer)
                                        ? ` (Admin: ${offer.adminApproval === "approved" ? "Approved" : "Rejected"
                                        })`
                                        : ""
                                        }`,
                                    // Only include withdrawReason if it exists, so the panel can hide the "Reason" line.
                                    ...(hasWithdrawReason ? { withdrawReason: offer.withdrawReason } : {}),
                                    submittedAt: offer.submittedAt,
                                    badge: active ? { subtitle: "Status", title: "ACTIVE" } : null,
                                }}
                                toAbsoluteUrl={toAbsoluteUrl}
                                onViewProfile={
                                    playerSlug ? () => navigateToProfile(playerSlug) : undefined
                                }
                                actions={
                                    offer.status === "pending" ? (
                                        <button
                                            onClick={() => handleWithdraw(offer.id, offer.status)}
                                            className="withdraw-button"
                                        >
                                            Withdraw Offer
                                        </button>
                                    ) : null
                                }
                                footer={
                                    active ? (
                                        <div>
                                            <strong>✅ This contract is now active.</strong>
                                            <br />
                                            {renderCountdown(offer.expiryDate)}
                                        </div>
                                    ) : null
                                }
                            />
                        </div>
                    );
                })
            )}
        </div>
    );
};

export default ContractOffer;






